import React, { useEffect, useState } from 'react';
import { useGame, api } from '../context/GameContext';
import { X, Plus, Coins, Trash2, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import ChessRook from './ChessRook';
import { SetBadges } from './CollectionSetBadges';
import thumb from '../utils/thumb';

const fmt = (n) => (n || 0).toLocaleString('en-US');

const RARITY_COLORS = {
  common: '#9CA3AF', uncommon: '#10B981', rare: '#3B82F6',
  legendary: '#A855F7', masterpiece: '#F59E0B',
};

// Manage one collection: rename, expand slots (coin sink), add/remove
// paintings. Nothing here changes a painting's gameplay state.
const CollectionManageSheet = ({ collection, onClose, onChanged, onDelete }) => {
  const { user, language, refreshUser, haptic } = useGame();
  const ru = language === 'ru';
  const [coll, setColl] = useState(collection);
  const [picking, setPicking] = useState(false);
  const [available, setAvailable] = useState([]);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setColl(collection);
    setName(collection?.name || '');
    setPicking(false);
    setRenaming(false);
    // Keyed on id only: the parent reloads its list after every change,
    // and resetting on object identity would snap the picker shut after
    // each added painting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection?.id]);

  useEffect(() => {
    if (!picking) return;
    (async () => {
      try {
        const [cardsRes, collsRes] = await Promise.all([
          api.get('/cards/me'),
          api.get('/collections/me'),
        ]);
        const taken = new Set(
          (collsRes.data?.collections || []).flatMap((c) => (c.cards || []).map((x) => x.id)),
        );
        // A collection can't hold two copies of the same painting, so
        // hide duplicates of what's already inside this one.
        const twins = new Set((coll?.cards || []).map((c) => c.template_id).filter(Boolean));
        setAvailable(
          (cardsRes.data || []).filter((c) => !taken.has(c.id) && !twins.has(c.template_id)),
        );
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picking]);

  if (!coll) return null;

  const freeSlots = coll.slots - coll.cards_count;

  const addCard = async (card) => {
    haptic?.();
    setBusy(true);
    try {
      const { data } = await api.post(`/collections/${coll.id}/cards`, { card_id: card.id });
      setColl(data.collection);
      setAvailable((prev) => prev.filter(
        (c) => c.id !== card.id && !(card.template_id && c.template_id === card.template_id),
      ));
      onChanged?.();
      if (data.collection.cards_count >= data.collection.slots) setPicking(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const removeCard = async (card) => {
    haptic?.();
    setBusy(true);
    try {
      const { data } = await api.delete(`/collections/${coll.id}/cards/${card.id}`);
      setColl(data.collection);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const buySlot = async () => {
    if (!coll.next_slot_cost) return;
    haptic?.();
    setBusy(true);
    try {
      const { data } = await api.post(`/collections/${coll.id}/slots`);
      setColl((p) => ({ ...p, slots: data.slots, next_slot_cost: data.next_slot_cost }));
      toast.success(ru
        ? `Слот куплен (−${fmt(data.cost)}). Слотов: ${data.slots}`
        : `Slot unlocked (−${fmt(data.cost)}). Slots: ${data.slots}`);
      onChanged?.();
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (ru ? 'Недостаточно монет' : 'Not enough coins'));
    } finally {
      setBusy(false);
    }
  };

  const saveName = async () => {
    const next = name.trim();
    if (!next || next === coll.name) { setRenaming(false); return; }
    try {
      await api.patch(`/collections/${coll.id}`, { name: next });
      setColl((p) => ({ ...p, name: next }));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setRenaming(false);
    }
  };

  const canAffordSlot = (user?.coins || 0) >= (coll.next_slot_cost || 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="collection-sheet">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-[3px]" onClick={onClose} />
      <div
        className="relative w-full sm:max-w-sm bg-[#0E0E18] border border-[#D4AF37]/25 rounded-t-2xl sm:rounded-2xl px-4 pt-3 max-h-[85vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
        style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-[#3F3F46]/70 mb-3 sm:hidden" />

        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            {renaming ? (
              <div className="flex items-center gap-2">
                <input
                  data-testid="collection-rename-input"
                  autoFocus
                  value={name}
                  maxLength={40}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveName()}
                  className="flex-1 bg-[#0a0a12] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-[#F5F5F7] outline-none focus:border-[#D4AF37]/60"
                />
                <button data-testid="collection-rename-save" onClick={saveName} className="p-1.5 rounded-lg bg-[#D4AF37]/15">
                  <Check className="w-4 h-4 text-[#D4AF37]" />
                </button>
              </div>
            ) : (
              <h2
                data-testid="collection-title"
                onClick={() => setRenaming(true)}
                className="font-heading text-[17px] text-white leading-tight truncate cursor-text"
              >
                {coll.name}
              </h2>
            )}
            <div className="text-[10px] text-[#A1A1AA]/80 uppercase tracking-[0.16em] mt-1 flex items-center gap-2.5">
              <span className="font-mono">{coll.cards_count}/{coll.slots}</span>
              <span className="flex items-center gap-1">
                <ChessRook className="w-3 h-3 text-[#D4AF37]" />
                <span className="font-mono text-[#D4AF37]">{fmt(coll.total_influence)}</span>
              </span>
              {coll.minted_count > 0 && (
                <span className="flex items-center gap-1 text-[#0098EA]">
                  <Sparkles className="w-3 h-3" />
                  <span className="font-mono">{coll.minted_count}</span>
                </span>
              )}
            </div>
          </div>
          <button data-testid="collection-sheet-close" onClick={onClose} className="p-1 rounded-full hover:bg-white/5">
            <X className="w-5 h-5 text-[#A1A1AA]" />
          </button>
        </div>

        {/* Qualifying sets */}
        {coll.sets?.length > 0 && (
          <div className="mb-3" data-testid="collection-sets">
            <SetBadges sets={coll.sets} language={ru ? 'ru' : 'en'} />
          </div>
        )}

        {/* Paintings in the collection */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {coll.cards.map((card) => (
            <div key={card.id} className="relative" data-testid={`collection-card-${card.id}`}>
              <img
                src={thumb(card.image_url, 160)}
                alt={card.title}
                loading="lazy"
                className="w-full aspect-[3/4] object-cover rounded-lg border"
                style={{
                  borderColor: `${RARITY_COLORS[card.rarity] || '#9CA3AF'}55`,
                  objectPosition: `${card.image_focus_x ?? 50}% ${card.image_focus_y ?? 50}%`,
                }}
              />
              <button
                data-testid={`collection-remove-${card.id}`}
                onClick={() => removeCard(card)}
                disabled={busy}
                className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#0E0E18] border border-[#EF4444]/50 flex items-center justify-center active:scale-90 transition-transform"
              >
                <X className="w-3 h-3 text-[#EF4444]" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/70 rounded-b-lg">
                <div className="text-[9px] text-[#F5F5F7] truncate">{card.title}</div>
              </div>
              {card.set_bonus > 0 && (
                <div
                  data-testid={`collection-card-bonus-${card.id}`}
                  className="absolute top-1 left-1 px-1 rounded text-[9px] font-bold"
                  style={{ background: 'rgba(16,185,129,0.9)', color: '#04120c' }}
                >
                  +{card.set_bonus}%
                </div>
              )}
            </div>
          ))}
          {freeSlots > 0 && (
            <button
              data-testid="collection-add-card-btn"
              onClick={() => { haptic?.(); setPicking((p) => !p); }}
              className="w-full aspect-[3/4] rounded-lg border border-dashed border-[#D4AF37]/40 flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform"
            >
              <Plus className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[9px] text-[#A1A1AA]">{ru ? 'Добавить' : 'Add'}</span>
            </button>
          )}
        </div>

        {/* Painting picker */}
        {picking && (
          <div className="mb-3" data-testid="collection-picker">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#A1A1AA]/70 mb-1.5">
              {ru ? 'Свободные картины' : 'Available paintings'}
            </div>
            {available.length === 0 ? (
              <div className="text-xs text-[#A1A1AA] py-4 text-center">
                {ru ? 'Все картины уже в коллекциях' : 'All paintings are already in collections'}
              </div>
            ) : (
              <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {available.map((card) => (
                  <button
                    key={card.id}
                    data-testid={`collection-pick-${card.id}`}
                    onClick={() => addCard(card)}
                    disabled={busy}
                    className="flex-shrink-0 w-16 active:scale-95 transition-transform"
                  >
                    <img
                      src={thumb(card.image_url, 160)}
                      alt={card.title}
                      loading="lazy"
                      className="w-16 h-20 object-cover rounded-md border border-white/10"
                      style={{ objectPosition: `${card.image_focus_x ?? 50}% ${card.image_focus_y ?? 50}%` }}
                    />
                    <div className="text-[9px] text-[#A1A1AA] truncate mt-0.5">{card.title}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Slot expansion */}
        {coll.next_slot_cost ? (
          <button
            data-testid="collection-buy-slot-btn"
            onClick={buySlot}
            disabled={busy || !canAffordSlot}
            className={`w-full py-2.5 rounded-xl flex items-center justify-center gap-2 mb-2 transition-transform active:scale-[0.98] ${
              canAffordSlot
                ? 'bg-gradient-to-r from-[#D4AF37] to-[#F5D07A] text-[#0C0C14]'
                : 'glass-card border border-[#D4AF37]/25 text-[#A1A1AA]'
            }`}
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-bold">
              {ru ? 'Купить слот' : 'Buy slot'} · {fmt(coll.next_slot_cost)}
            </span>
            <Coins className="w-3.5 h-3.5 opacity-70" />
          </button>
        ) : (
          <div className="w-full py-2.5 rounded-xl glass-card border border-[#52525B]/30 text-center mb-2 text-xs text-[#A1A1AA]">
            {ru ? `Максимум слотов (${coll.max_slots})` : `Max slots reached (${coll.max_slots})`}
          </div>
        )}

        <button
          data-testid="collection-delete-btn"
          onClick={() => onDelete?.(coll)}
          className="w-full py-2 flex items-center justify-center gap-1.5 text-[11px] text-[#EF4444]/70 hover:text-[#EF4444] transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          {ru ? 'Удалить коллекцию (без возврата монет)' : 'Delete collection (no refund)'}
        </button>
      </div>
    </div>
  );
};

export default CollectionManageSheet;
